import { HttpHeaders } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ToastService } from '@app/shared/service/toast.service';
import { environment } from '@env/environment';
import { DalService } from '@upr-web-primer/dal';
import { IGuestsResponse } from '@upr-web-primer/ms-guests';
import { NotificationService } from '@upr-web-primer/notification';
import { LocalStorageService } from '@upr-web-primer/storage';
import { each } from '@upr-web-primer/udx-utils';
import { WINDOW } from '@upr-web-primer/window';
import { CommonUtilityService } from '../../services/common-utility.service';
import mockData from '../../shared/constants/mockData.json';

@Component({
  selector: 'app-unsubscribe-email',
  templateUrl: './unsubscribe-email.component.html',
  styleUrls: ['./unsubscribe-email.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnsubscribeEmailComponent implements OnInit {
  pageLabels: any;
  mockData = mockData;
  @Input() subscriptionForm: FormGroup;
  emailErrorMsgInfo: any;
  userProfileData: IGuestsResponse;
  userData: any;
  fullName: string;
  addressData: any;
  contactsData: any;
  mailData: any;
  phoneData: any;
  resortAreaCode: any;
  savedSourceId: string;
  constructor(
    public common: CommonUtilityService,
    @Inject('GlobalDefaults') public globalDefault: any,
    @Inject('Ranges') public ranges: any,
    public cdRef: ChangeDetectorRef,
    @Inject('EmailPasswordPattern') public emailRegex: any,
    private readonly formBuilder: FormBuilder,
    @Inject('sourceKeys') public sourceKeys: any,
    private readonly notification: NotificationService,
    private readonly dal: DalService,
    @Inject('AppDefaults') public appDefault: any,
    private readonly title: Title,
    public localStorage: LocalStorageService,
    @Inject('RelativeUrls') private readonly relativeUrls: any,
    @Inject(WINDOW) public window: any,
    private readonly toastService: ToastService,
  ) {
    this.pageLabels = this.common.getResolvedPageData();
    this.title.setTitle(this.pageLabels?.['page.unsubscribeEmail']);
    this.resortAreaCode = this.appDefault.RESORT_AREA_CODE;
  }

  ngOnInit(): void {
    this.createForm();
  }

  createForm(): void {
    this.subscriptionForm = this.formBuilder.group({
      email: new FormControl('', [
        Validators.required,
        Validators.pattern(this.emailRegex.EMAIL),
      ]),
    });
  }

  getErrorMessageInfo(event: any): void {
    const fieldInfo = event?.target?.id;

    const emailValue = this.subscriptionForm.value.email;
    if (emailValue.length >= this.ranges.MAX.EMAIL) {
      this.subscriptionForm.controls[this.globalDefault.EMAIL].setValue(
        this.subscriptionForm.value.email.substring(0, this.ranges.MAX.EMAIL),
      );
    }
    this.emailErrorMsgInfo = this.getErrorMessage(fieldInfo);

    this.cdRef.markForCheck();
  }

  getErrorMessage(field: any): string {
    const fieldName = this.subscriptionForm?.get(field);

    const emptyCheck = this.pageLabels?.['errCode.E001'];

    fieldName?.markAsTouched();

    if (!fieldName?.valid && (fieldName?.dirty || fieldName?.touched)) {
      switch (field) {
        case this.globalDefault.EMAIL: {
          if (!this.subscriptionForm.value.email) return emptyCheck;
          else if (
            this.subscriptionForm.value.email.length < this.ranges.MAX.EMAIL &&
            this.subscriptionForm.value.email.match(
              this.emailRegex.EMAIL_SIGNUP,
            ) == null
          ) {
            return this.pageLabels?.['errCode.E021'];
          } else if (
            this.subscriptionForm.value.email.length < this.ranges.MAX.EMAIL
          ) {
            return '';
          } else {
            return this.pageLabels?.['errCode.E021'];
          }
        }
      }
    }
    return '';
  }

  navigateTo(): any {
    const type = this.getResortType(this.resortAreaCode);
    const sourceKey = this.getSourceKey(this.resortAreaCode);
    this.savedSourceId = this.common?.getSourceId(sourceKey);
    const instanceId = this.localStorage.getItem('InteractGuestId');
    let httpHeaders = new HttpHeaders({});
    httpHeaders = httpHeaders.append('x-instance-id', instanceId);
    httpHeaders = httpHeaders.append('x-source-id', this.savedSourceId);

    const request = {
      email_address: this.subscriptionForm.value.email,
      ...(environment.emailOffers !== ''
        ? {
            opt_in_out: [
              {
                type: type,
                opt_in: false,
              },
            ],
          }
        : {
            email_offers_promos: false,
          }),
    };

    const url = this.relativeUrls?.EMAIL_OPT_IN_OUT;
    this.dal
      .httpRequest(url, this.appDefault.POST, request, httpHeaders)
      .then(() => {
        this.notification?.show(
          this.pageLabels?.['unsubscribe.success'],
          this.globalDefault.SUCCESS,
        );
        this.cdRef.markForCheck();
      })
      .catch((err: any) => {
        this.notification?.show(err.error.detail);
        this.common.eventDDlTrigger(err.error.detail);
      });
  }

  getResortType = (resort: string): string => {
    if (!resort) {
      return '';
    }
    return `${resort.toUpperCase()}_${environment.emailOffers}`;
  };

  getSourceKey = (resort: string): string => {
    if (!resort) {
      return '';
    }
    return `Web${this.toTitleCase(resort)}${
      environment.emailUnsubscribeOrEdit
    }`;
  };

  toTitleCase(str: string | undefined): string {
    if (str === undefined) {
      return '';
    }
    return str
      .toLowerCase()
      .replace(/\b\w/g, (firstChar) => firstChar.toUpperCase());
  }

  fetchProfileInfo(
    addressList: any,
    phoneList: any,
    mailData: any,
    contachData: any,
  ): void {
    each(addressList, (element: any) => {
      if (element?.primary) {
        this.addressData = element;
      }
    });
    each(contachData, (element: any) => {
      if (element?.primary == true) {
        this.contactsData = element;
        this.fullName = `${this.contactsData?.last_name}  ${this.contactsData?.first_name}`;
      }
    });
    each(mailData, (element: any) => {
      if (element?.primary) {
        this.mailData = element;
      }
    });
    each(phoneList, (element: any) => {
      if (element?.primary) {
        this.phoneData = element;
      }
    });
  }

  public getRelativeUrl(url: string, areaCode: string): string {
    return url.replace('{resort-area-code}', areaCode);
  }

  eventDDlTriggerModal(errorInfo: any): any {
    this.common.invokeFireEvent(
      'Modal',
      errorInfo,
      'Modal',
      'null',
      new Date().toISOString(),
      'Toast',
      '',
      this.window?.digitalData?.page?.category?.primaryCategory,
      this.window?.digitalData?.page?.category?.subCategory1,
    );
  }
  emailValidation(event: any): any {
    const emailId = event?.target?.value;
    this.postEmailValidate(emailId);
  }

  postEmailValidate(emailId: any): any {
    const httpHeaders = new HttpHeaders()
      .append('x-instance-id', '123')
      .append('x-source-id', '1234');

    const request = {
      email_address: emailId,
    };
    const url = this.getRelativeUrl(
      this.relativeUrls.EMAIL_VALIDATE,
      this.appDefault.USJ,
    );
    this.dal
      .httpRequest(url, this.appDefault.POST, request, httpHeaders)
      .then((data: any) => {
        const verdict = data?.verdict;
        if (verdict === this.globalDefault?.RISKY) {
          if (this.hasSuggestion(data)) {
            const msg = `${this.pageLabels?.['email.risky_with_suggestion_snack']} ${data?.suggestion}`;

            this.triggerToastAndEvent(msg, msg);
          } else {
            const msg = this.pageLabels?.['email.risky_wo_suggestion_snack'];

            this.triggerToastAndEvent(msg, msg);
          }
        }

        if (verdict === this.globalDefault?.INVALID) {
          const msg = this.pageLabels?.['email.invalid_snack'];

          this.triggerToastAndEvent(msg, this.globalDefault?.SUCCESS);
        }
      })
      .catch((error: any) => {
        if (error.status === this.globalDefault?.ERROR_422) {
          this.toastService.show({
            text: this.pageLabels?.['email.invalid_snack'],
            type: this.globalDefault?.SUCCESS,
          });
        }
      });
  }
  private triggerToastAndEvent(
    text: string,
    eventText: string,
    type: any = this.globalDefault?.SUCCESS,
  ): void {
    this.eventDDlTriggerModal(eventText);

    this.toastService?.show({
      text,
      type,
    });

    sessionStorage.setItem('toastSession', 'true');
  }

  private hasSuggestion(data: any): boolean {
    return this.globalDefault?.SEARCH_KEY in data && data?.suggestion !== '';
  }
}
